import { db, storage } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { createAssignmentNotification } from './notificationService';

/**
 * Submission Service for managing student deliverable submissions
 */

/**
 * Submit a deliverable
 * @param {Object} submissionData - Submission data
 * @param {File} file - Optional file to upload
 * @returns {Promise<string>} - Submission ID
 */
export const submitDeliverable = async (submissionData, file = null) => {
  try {
    let fileUrl = '';
    let fileName = '';
    let fileType = '';
    let fileSize = 0;

    // Upload file to Firebase Storage if provided
    if (file) {
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storageRef = ref(storage, 
        `submissions/${submissionData.projectId}/${submissionData.deliverableId}/${submissionData.studentId}_${timestamp}_${sanitizedFileName}`
      );
      
      await uploadBytes(storageRef, file);
      fileUrl = await getDownloadURL(storageRef);
      fileName = file.name;
      fileType = file.type;
      fileSize = file.size;
    }

    // Create submission document
    const submission = {
      deliverableId: submissionData.deliverableId,
      projectId: submissionData.projectId,
      studentId: submissionData.studentId,
      studentName: submissionData.studentName || 'Unknown Student',
      content: submissionData.content || '',
      fileUrl,
      fileName,
      fileType,
      fileSize,
      submittedAt: serverTimestamp(),
      status: 'submitted',
      feedback: '',
      version: 1,
      isLatest: true
    };

    const submissionRef = await addDoc(collection(db, 'submissions'), submission);

    // Create activity log
    await addDoc(collection(db, 'activities'), {
      projectId: submissionData.projectId,
      type: 'submission_created',
      description: `Student submitted deliverable: ${submissionData.deliverableTitle || 'Assignment'}`,
      submissionId: submissionRef.id,
      studentId: submissionData.studentId,
      timestamp: serverTimestamp()
    });

    return submissionRef.id;
  } catch (error) {
    console.error('Error submitting deliverable:', error);
    throw new Error('Failed to submit assignment: ' + error.message);
  }
};

/**
 * Get submissions for a deliverable
 * @param {string} deliverableId - Deliverable ID
 * @returns {Promise<Array>} - Array of submissions
 */
export const getDeliverableSubmissions = async (deliverableId) => {
  try {
    const submissionsQuery = query(
      collection(db, 'submissions'),
      where('deliverableId', '==', deliverableId),
      where('isLatest', '==', true),
      orderBy('submittedAt', 'desc')
    );

    const querySnapshot = await getDocs(submissionsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching deliverable submissions:', error);
    throw new Error('Failed to fetch submissions: ' + error.message);
  }
};

/**
 * Get submissions for a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Array>} - Array of submissions
 */
export const getProjectSubmissions = async (projectId) => {
  try {
    const submissionsQuery = query(
      collection(db, 'submissions'),
      where('projectId', '==', projectId),
      where('isLatest', '==', true),
      orderBy('submittedAt', 'desc')
    );

    const querySnapshot = await getDocs(submissionsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching project submissions:', error);
    throw new Error('Failed to fetch submissions: ' + error.message);
  }
};

/**
 * Get student's submission for a specific deliverable
 * @param {string} studentId - Student ID
 * @param {string} deliverableId - Deliverable ID
 * @returns {Promise<Object|null>} - Submission data or null
 */
export const getStudentSubmission = async (studentId, deliverableId) => {
  try {
    const submissionsQuery = query(
      collection(db, 'submissions'),
      where('studentId', '==', studentId),
      where('deliverableId', '==', deliverableId),
      where('isLatest', '==', true)
    );

    const querySnapshot = await getDocs(submissionsQuery);
    
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.error('Error fetching student submission:', error);
    throw new Error('Failed to fetch submission: ' + error.message);
  }
};

/**
 * Update submission with feedback
 * @param {string} submissionId - Submission ID
 * @param {string} feedback - Feedback text
 * @param {string} status - New status (approved, rejected, needs_revision)
 * @param {string} reviewerId - ID of reviewer
 * @returns {Promise<void>}
 */
export const updateSubmissionFeedback = async (submissionId, feedback, status, reviewerId) => {
  try {
    // Update submission
    await updateDoc(doc(db, 'submissions', submissionId), {
      feedback,
      status,
      reviewedAt: serverTimestamp(),
      reviewedBy: reviewerId,
      updatedAt: serverTimestamp()
    });

    // Get submission data for activity log
    const submissionDoc = await getDoc(doc(db, 'submissions', submissionId));
    const submissionData = submissionDoc.data();

    // Create activity log
    await addDoc(collection(db, 'activities'), {
      projectId: submissionData.projectId,
      type: 'submission_reviewed',
      description: `Submission ${status} with feedback`,
      submissionId,
      studentId: submissionData.studentId,
      reviewerId,
      timestamp: serverTimestamp()
    });

  } catch (error) {
    console.error('Error updating submission feedback:', error);
    throw new Error('Failed to update feedback: ' + error.message);
  }
};

/**
 * Resubmit a deliverable (create new version)
 * @param {string} originalSubmissionId - Original submission ID
 * @param {Object} submissionData - New submission data
 * @param {File} file - Optional new file
 * @returns {Promise<string>} - New submission ID
 */
export const resubmitDeliverable = async (originalSubmissionId, submissionData, file = null) => {
  try {
    // Get original submission to increment version
    const originalDoc = await getDoc(doc(db, 'submissions', originalSubmissionId));
    const originalData = originalDoc.data();

    // Mark original as not latest
    await updateDoc(doc(db, 'submissions', originalSubmissionId), {
      isLatest: false
    });

    // Create new submission with incremented version
    const newSubmissionData = {
      ...submissionData,
      version: (originalData.version || 1) + 1,
      parentSubmissionId: originalSubmissionId
    };

    return await submitDeliverable(newSubmissionData, file);
  } catch (error) {
    console.error('Error resubmitting deliverable:', error);
    throw new Error('Failed to resubmit assignment: ' + error.message);
  }
};

/**
 * Delete a submission
 * @param {string} submissionId - Submission ID
 * @param {string} projectId - Project ID
 * @param {string} userId - User ID who is deleting
 * @returns {Promise<void>}
 */
export const deleteSubmission = async (submissionId, projectId, userId) => {
  try {
    // Get submission data first
    const submissionDoc = await getDoc(doc(db, 'submissions', submissionId));
    if (!submissionDoc.exists()) {
      throw new Error('Submission not found');
    }

    const submissionData = submissionDoc.data();

    // Delete file from storage if it exists
    if (submissionData.fileUrl) {
      try {
        const fileRef = ref(storage, submissionData.fileUrl);
        await deleteObject(fileRef);
      } catch (storageError) {
        console.warn('Could not delete file from storage:', storageError);
      }
    }

    // Soft delete submission
    await updateDoc(doc(db, 'submissions', submissionId), {
      isLatest: false,
      deletedAt: serverTimestamp(),
      deletedBy: userId
    });

    // Create activity log
    await addDoc(collection(db, 'activities'), {
      projectId,
      type: 'submission_deleted',
      description: `Submission deleted`,
      submissionId,
      studentId: submissionData.studentId,
      userId,
      timestamp: serverTimestamp()
    });

  } catch (error) {
    console.error('Error deleting submission:', error);
    throw new Error('Failed to delete submission: ' + error.message);
  }
};

/**
 * Get submission analytics for a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} - Analytics data
 */
export const getSubmissionAnalytics = async (projectId) => {
  try {
    const submissions = await getProjectSubmissions(projectId);
    
    const analytics = {
      totalSubmissions: submissions.length,
      statusBreakdown: {
        submitted: 0,
        approved: 0,
        rejected: 0,
        needs_revision: 0
      },
      averageSubmissionTime: 0,
      onTimeSubmissions: 0,
      lateSubmissions: 0
    };

    // Count by status
    submissions.forEach(submission => {
      const status = submission.status || 'submitted';
      analytics.statusBreakdown[status] = (analytics.statusBreakdown[status] || 0) + 1;
    });

    return analytics;
  } catch (error) {
    console.error('Error getting submission analytics:', error);
    throw new Error('Failed to get analytics: ' + error.message);
  }
};

// File type utilities – returns slug for use with FileTypeIcon component
export const getSubmissionFileIcon = (fileType) => {
  if (!fileType) return 'file';
  const type = fileType.toLowerCase();
  if (type.includes('pdf')) return 'pdf';
  if (type.includes('word') || type.includes('doc')) return 'doc';
  if (type.includes('excel') || type.includes('sheet') || type.includes('csv')) return 'sheet';
  if (type.includes('powerpoint') || type.includes('ppt')) return 'sheet';
  if (type.includes('image') || type.includes('png') || type.includes('jpg') || type.includes('jpeg')) return 'image';
  if (type.includes('video') || type.includes('mp4')) return 'video';
  if (type.includes('audio') || type.includes('mp3')) return 'audio';
  if (type.includes('zip') || type.includes('rar')) return 'zip';
  if (type.includes('html') || type.includes('css') || type.includes('js')) return 'code';
  return 'file';
};

export const formatSubmissionFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const validateSubmissionFile = (file, maxSizeMB = 50) => {
  const errors = [];
  
  if (!file) {
    return errors;
  }
  
  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    errors.push(`File size must be less than ${maxSizeMB}MB`);
  }
  
  // Check file type (allow most common submission types)
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'image/jpeg',
    'image/png',
    'image/gif',
    'video/mp4',
    'video/quicktime',
    'application/zip',
    'application/x-zip-compressed'
  ];
  
  if (!allowedTypes.includes(file.type)) {
    errors.push('File type not supported. Please use PDF, Word, Excel, PowerPoint, images, videos, or ZIP files.');
  }
  
  return errors;
};

/**
 * Submit an assignment
 * @param {Object} submissionData - Submission data
 * @returns {Promise<string>} - Submission ID
 */
export const submitAssignment = async (submissionData) => {
  try {
    // Validate submission data
    if (!submissionData.projectId || !submissionData.studentId || !submissionData.deliverableId) {
      throw new Error('Missing required submission data');
    }

    let fileUrl = null;
    let fileName = null;
    let fileType = null;
    let fileSize = null;

    // Handle file upload if present
    if (submissionData.file) {
      const file = submissionData.file;
      
      // Validate file
      const validationResult = validateSubmissionFile(file);
      if (!validationResult.isValid) {
        throw new Error(validationResult.error);
      }

      // Upload file
      const timestamp = Date.now();
      const sanitizedFileName = sanitizeFileName(file.name);
      const filePath = `submissions/${submissionData.projectId}/${submissionData.studentId}/${timestamp}_${sanitizedFileName}`;
      
      const fileRef = ref(storage, filePath);
      await uploadBytes(fileRef, file);
      fileUrl = await getDownloadURL(fileRef);
      fileName = file.name;
      fileType = file.type;
      fileSize = file.size;
    }

    // Check for existing submission for this deliverable
    const existingSubmissionQuery = query(
      collection(db, 'submissions'),
      where('projectId', '==', submissionData.projectId),
      where('studentId', '==', submissionData.studentId),
      where('deliverableId', '==', submissionData.deliverableId)
    );

    const existingSubmissions = await getDocs(existingSubmissionQuery);
    
    if (!existingSubmissions.empty) {
      // Update existing submission (resubmission)
      const existingSubmission = existingSubmissions.docs[0];
      const submissionRef = existingSubmission.ref;
      
      // Archive old file if exists and we have a new one
      const oldData = existingSubmission.data();
      if (oldData.fileUrl && fileUrl && oldData.fileUrl !== fileUrl) {
        try {
          const oldFileRef = ref(storage, oldData.fileUrl);
          await deleteObject(oldFileRef);
        } catch (error) {
          console.log('Could not delete old file:', error);
        }
      }

      const updateData = {
        content: submissionData.content || '',
        ...(fileUrl && {
          fileUrl,
          fileName,
          fileType,
          fileSize
        }),
        status: 'submitted',
        submittedAt: serverTimestamp(),
        feedback: '',
        version: (oldData.version || 1) + 1,
        previousVersions: [
          ...(oldData.previousVersions || []),
          {
            submittedAt: oldData.submittedAt,
            content: oldData.content,
            fileUrl: oldData.fileUrl,
            fileName: oldData.fileName,
            version: oldData.version || 1
          }
        ]
      };

      await updateDoc(submissionRef, updateData);

      // Create activity log
      await addDoc(collection(db, 'activities'), {
        projectId: submissionData.projectId,
        studentId: submissionData.studentId,
        type: 'submission_resubmitted',
        description: `Student resubmitted assignment`,
        deliverableId: submissionData.deliverableId,
        submissionId: existingSubmission.id,
        timestamp: serverTimestamp()
      });

      return existingSubmission.id;
    } else {
      // Create new submission
      const submission = {
        projectId: submissionData.projectId,
        studentId: submissionData.studentId,
        deliverableId: submissionData.deliverableId,
        content: submissionData.content || '',
        fileUrl,
        fileName,
        fileType,
        fileSize,
        status: 'submitted',
        submittedAt: serverTimestamp(),
        feedback: '',
        version: 1,
        previousVersions: []
      };

      const submissionRef = await addDoc(collection(db, 'submissions'), submission);

      // Create activity log
      await addDoc(collection(db, 'activities'), {
        projectId: submissionData.projectId,
        studentId: submissionData.studentId,
        type: 'submission_created',
        description: `Student submitted assignment`,
        deliverableId: submissionData.deliverableId,
        submissionId: submissionRef.id,
        timestamp: serverTimestamp()
      });

      return submissionRef.id;
    }
  } catch (error) {
    console.error('Error submitting assignment:', error);
    throw new Error('Failed to submit assignment: ' + error.message);
  }
};

/**
 * Provide feedback on a submission
 * @param {string} submissionId - Submission ID
 * @param {string} feedback - Feedback text
 * @param {string} status - New status (approved, rejected)
 * @param {string} companyId - Company ID providing feedback
 * @returns {Promise<void>}
 */
export const provideFeedback = async (submissionId, feedback, status, companyId) => {
  try {
    // Get submission data
    const submissionDoc = await getDoc(doc(db, 'submissions', submissionId));
    if (!submissionDoc.exists()) {
      throw new Error('Submission not found');
    }

    const submissionData = submissionDoc.data();

    // Update submission
    await updateDoc(doc(db, 'submissions', submissionId), {
      feedback,
      status,
      reviewedAt: serverTimestamp(),
      reviewedBy: companyId
    });

    // Create activity log
    await addDoc(collection(db, 'activities'), {
      projectId: submissionData.projectId,
      studentId: submissionData.studentId,
      companyId,
      type: 'submission_reviewed',
      description: `Assignment ${status}`,
      deliverableId: submissionData.deliverableId,
      submissionId,
      timestamp: serverTimestamp()
    });

    // Create notification for student
    try {
      // Get company name
      let companyName = 'Company';
      const companyDoc = await getDoc(doc(db, 'companyProfiles', companyId));
      if (companyDoc.exists()) {
        companyName = companyDoc.data().companyName || 'Company';
      }

      // Get assignment title
      let assignmentTitle = 'Assignment';
      const deliverableDoc = await getDoc(doc(db, 'deliverables', submissionData.deliverableId));
      if (deliverableDoc.exists()) {
        assignmentTitle = deliverableDoc.data().title || 'Assignment';
      }

      // Determine notification type
      const notificationType = status === 'approved' ? 'approved' : 
                              status === 'rejected' ? 'revision' : 'feedback';

      await createAssignmentNotification(
        submissionData.studentId,
        companyName,
        assignmentTitle,
        submissionData.projectId,
        notificationType
      );
    } catch (notificationError) {
      console.error('Error creating assignment notification:', notificationError);
      // Don't fail the feedback if notification fails
    }

  } catch (error) {
    console.error('Error providing feedback:', error);
    throw new Error('Failed to provide feedback: ' + error.message);
  }
}; 